import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {sweetAlertQuestion, sweetAlerts} from "../../utils/sweet-alerts";
import {getContractorFeedbackService} from "../../services/contractorServices";

const Feedback = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    feedbackType: '',
    subject: '',
    description: '',
    priority: 'medium',
    contactMethod: '',
    email: '',
    phone: '',
    allowContact: true,
    attachments: []
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const feedbackTypes = [
    {
      id: 'bug-report',
      title: 'Bug Report',
      description: 'Report technical issues or problems',
      icon: '🐛',
      color: 'danger'
    },
    {
      id: 'feature-request',
      title: 'Feature Request',
      description: 'Suggest new features or improvements',
      icon: '💡',
      color: 'primary'
    },
    {
      id: 'general-feedback',
      title: 'General Feedback',
      description: 'Share your thoughts and suggestions',
      icon: '💬',
      color: 'info'
    },
    {
      id: 'account-issue',
      title: 'Account Issue',
      description: 'Problems with your account or access',
      icon: '👤',
      color: 'warning'
    },
    {
      id: 'question',
      title: 'Question',
      description: 'Ask questions about how to use the platform',
      icon: '❓',
      color: 'secondary'
    },
    {
      id: 'compliment',
      title: 'Compliment',
      description: 'Share positive feedback about your experience',
      icon: '👏',
      color: 'success'
    }
  ];

  const priorityLevels = [
    { value: 'low', label: 'Low - General feedback', color: 'secondary' },
    { value: 'medium', label: 'Medium - Standard issue', color: 'warning' },
    { value: 'high', label: 'High - Important problem', color: 'danger' },
    { value: 'urgent', label: 'Urgent - Critical issue', color: 'danger' }
  ];

  const categories = {
    'bug-report': [
      'Login/Authentication',
      'Rating Submission',
      'Search Functionality',
      'Dashboard Issues',
      'Mobile App Problems',
      'Performance Issues',
      'Data Display Errors',
      'Other Technical Issue'
    ],
    'feature-request': [
      'Rating System',
      'Search & Filters',
      'User Interface',
      'Mobile Experience',
      'Notifications',
      'Reporting Tools',
      'Account Management',
      'Other Enhancement'
    ],
    'general-feedback': [
      'User Experience',
      'Design & Layout',
      'Content Quality',
      'Performance',
      'Customer Service',
      'Overall Platform',
      'Documentation',
      'Other Feedback'
    ],
    'account-issue': [
      'Login Problems',
      'Profile Settings',
      'Email Notifications',
      'Account Access',
      'Data Privacy',
      'Account Suspension',
      'Billing Issues',
      'Other Account Issue'
    ],
    'question': [
      'How to Submit Ratings',
      'Understanding Results',
      'Account Features',
      'Platform Policies',
      'Technical Questions',
      'Best Practices',
      'Getting Started',
      'Other Question'
    ],
    'compliment': [
      'Customer Service',
      'Platform Features',
      'User Experience',
      'Technical Performance',
      'Content Quality',
      'Overall Experience',
      'Team Recognition',
      'Other Compliment'
    ]
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    
    // Clear errors when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf', 'text/plain'];
      const maxSize = 5 * 1024 * 1024; // 5MB
      return validTypes.includes(file.type) && file.size <= maxSize;
    });

    setFormData(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...validFiles].slice(0, 3) // Max 3 files
    }));
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.feedbackType) {
      newErrors.feedbackType = 'Please select a feedback type';
    }

    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    } else if (formData.subject.length < 5) {
      newErrors.subject = 'Subject must be at least 5 characters';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.length < 20) {
      newErrors.description = 'Description must be at least 20 characters';
    }

    if (formData.allowContact && formData.contactMethod === 'email') {
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required for contact';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
    }

    if (formData.allowContact && formData.contactMethod === 'phone') {
      if (!formData.phone.trim()) {
        newErrors.phone = 'Phone number is required for contact';
      } else if (!/^[\d\s\-()+]{10,}$/.test(formData.phone.replace(/\s/g, ''))) {
        newErrors.phone = 'Please enter a valid phone number';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      // Add form fields
      formDataToSend.append('feedbackType', formData.feedbackType);
      formDataToSend.append('subject', formData.subject);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('priority', formData.priority);
      formDataToSend.append('contactMethod', formData.contactMethod);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('allowContact', formData.allowContact);
      
      // Add file attachments
      formData.attachments.forEach((file, index) => {
        formDataToSend.append(`file_${index + 1}`, file);
      });

      const response = await getContractorFeedbackService(formDataToSend);

      if (response.data && response.status === true) {
        // const result = await response.json();
				setSubmitted(true);
			} else {
        setIsSubmitting(false);
				sweetAlerts("error", "Failed to submit feedback");
				return;
			}
    } catch (error) {
      setIsSubmitting(false);
      console.error('Failed to submit feedback:', error);
      sweetAlerts("error", "Failed to submit feedback. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTypeIcon = (type) => {
    const feedbackType = feedbackTypes.find(t => t.id === type);
    return feedbackType ? feedbackType.icon : '📝';
  };

  const getTypeColor = (type) => {
    const feedbackType = feedbackTypes.find(t => t.id === type);
    return feedbackType ? feedbackType.color : 'secondary';
  };

  if (submitted) {
    return (
      <div className="dashboard-page">
        <div className="page-header">
          <h2>Feedback Submitted</h2>
          <p>Thank you for your feedback! We'll review it and get back to you if needed.</p>
        </div>

        <div className="feedback-success">
          <div className="success-icon">
            <svg width="80" height="80" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h3>Feedback Successfully Submitted!</h3>
          <div className="success-details">
            <div className="detail-item">
              <strong>Type:</strong> {getTypeIcon(formData.feedbackType)} {feedbackTypes.find(t => t.id === formData.feedbackType)?.title}
            </div>
            <div className="detail-item">
              <strong>Subject:</strong> {formData.subject}
            </div>
            <div className="detail-item">
              <strong>Priority:</strong> <span className={`priority-badge priority-${formData.priority}`}>
                {priorityLevels.find(p => p.value === formData.priority)?.label}
              </span>
            </div>
            {formData.allowContact && (
              <div className="detail-item">
                <strong>Contact Method:</strong> {formData.contactMethod === 'email' ? `📧 ${formData.email}` : `📞 ${formData.phone}`}
              </div>
            )}
          </div>
          
          <div className="success-actions">
            <button 
              className="btn btn-primary"
              onClick={() => {
                setSubmitted(false);
                setFormData({
                  feedbackType: '',
                  subject: '',
                  description: '',
                  priority: 'medium',
                  contactMethod: '',
                  email: '',
                  phone: '',
                  allowContact: true,
                  attachments: []
                });
              }}
            >
              Submit Another Feedback
            </button>
            <button 
              className="btn btn-secondary"
              onClick={() => navigate('/contractor-dashboard')}
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <div className="page-header">
        <h2>Send Feedback</h2>
        <p>Help us improve the platform with your suggestions, bug reports, and questions</p>
      </div>

      <div className="feedback-container">
        <form onSubmit={handleSubmit} className="feedback-form">
          
          {/* Feedback Type Selection */}
          <div className="form-section">
            <label className="form-label">What type of feedback do you have?</label>
            <div className="radio-group">
              {feedbackTypes.map(type => (
                <label key={type.id} className="radio-option">
                  <input
                    type="radio"
                    name="feedbackType"
                    value={type.id}
                    checked={formData.feedbackType === type.id}
                    onChange={handleInputChange}
                  />
                  <span className="radio-label">
                    {type.title} - {type.description}
                  </span>
                </label>
              ))}
            </div>
            {errors.feedbackType && <div className="error-message">{errors.feedbackType}</div>}
          </div>


          {/* Subject */}
              <div className="form-section">
                <label className="form-label">Subject</label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  placeholder="Brief summary of your feedback..."
                  className={`form-input ${errors.subject ? 'error' : ''}`}
                  maxLength={100}
                />
                <div className="character-count">{formData.subject.length}/100</div>
                {errors.subject && <div className="error-message">{errors.subject}</div>}
              </div>

              {/* Description */}
              <div className="form-section">
                <label className="form-label">Detailed Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Please provide detailed information about your feedback. Include steps to reproduce for bugs, specific use cases for features, or any other relevant details..."
                  className={`form-textarea ${errors.description ? 'error' : ''}`}
                  rows={6}
                  maxLength={2000}
                />
                <div className="character-count">{formData.description.length}/2000</div>
                {errors.description && <div className="error-message">{errors.description}</div>}
              </div>

              {/* File Attachments */}
              <div className="form-section">
                <label className="form-label">Attachments (Optional)</label>
                <div className="file-upload-area">
                  <input
                    type="file"
                    id="file-upload"
                    multiple
                    accept="image/*,.pdf,.txt"
                    onChange={handleFileUpload}
                    className="file-input"
                  />
                  <label htmlFor="file-upload" className="file-upload-label">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                      <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M7 10L12 15L17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M12 15V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>Click to upload files</span>
                    <small>Images, PDFs, or text files (max 5MB each, 3 files total)</small>
                  </label>
                </div>
                
                {formData.attachments.length > 0 && (
                  <div className="attachments-list">
                    {formData.attachments.map((file, index) => (
                      <div key={index} className="attachment-item">
                        <span className="file-name">{file.name}</span>
                        <span className="file-size">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                        <button 
                          type="button"
                          onClick={() => removeAttachment(index)}
                          className="remove-attachment"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Contact Preferences */}
              <div className="form-section">
                <div className="contact-section">
                  <div className="contact-toggle">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="allowContact"
                        checked={formData.allowContact}
                        onChange={handleInputChange}
                      />
                      <span className="checkmark"></span>
                      Allow us to contact you about this feedback
                    </label>
                  </div>

                  {formData.allowContact && (
                    <div className="contact-methods">
                      <label className="form-label">How should we contact you?</label>
                      <div className="contact-options">
                        <label className="contact-option">
                          <input
                            type="radio"
                            name="contactMethod"
                            value="email"
                            checked={formData.contactMethod === 'email'}
                            onChange={handleInputChange}
                          />
                          <span className="contact-icon">📧</span>
                          <span>Email</span>
                        </label>
                        <label className="contact-option">
                          <input
                            type="radio"
                            name="contactMethod"
                            value="phone"
                            checked={formData.contactMethod === 'phone'}
                            onChange={handleInputChange}
                          />
                          <span className="contact-icon">📞</span>
                          <span>Phone</span>
                        </label>
                      </div>

                      {formData.contactMethod === 'email' && (
                        <div className="contact-input">
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            placeholder="your.email@example.com"
                            className={`form-input ${errors.email ? 'error' : ''}`}
                          />
                          {errors.email && <div className="error-message">{errors.email}</div>}
                        </div>
                      )}

                      {formData.contactMethod === 'phone' && (
                        <div className="contact-input">
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            placeholder="(555) 123-4567"
                            className={`form-input ${errors.phone ? 'error' : ''}`}
                          />
                          {errors.phone && <div className="error-message">{errors.phone}</div>}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Submit Button */}
              <div className="form-submit">
                <button 
                  type="submit" 
                  className={`btn btn-large btn-${getTypeColor(formData.feedbackType)}`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <div className="spinner-small"></div>
                      Submitting Feedback...
                    </>
                  ) : (
                    <>
                      {getTypeIcon(formData.feedbackType)} Submit Feedback
                    </>
                  )}
                </button>
              </div>
        </form>

        {/* Help Sidebar */}
        <div className="feedback-sidebar">
          <div className="help-section">
            <h4>💡 Tips for Good Feedback</h4>
            <ul>
              <li><strong>Be specific:</strong> Include detailed steps or examples</li>
              <li><strong>Add context:</strong> Tell us what you were trying to accomplish</li>
              <li><strong>Include screenshots:</strong> Visual aids help us understand issues</li>
              <li><strong>Be constructive:</strong> Suggest improvements when possible</li>
            </ul>
          </div>

          <div className="help-section">
            <h4>🚀 Response Times</h4>
            <div className="response-times">
              <div className="response-item">
                <span className="priority-dot priority-urgent"></span>
                <span>Urgent: Within 4 hours</span>
              </div>
              <div className="response-item">
                <span className="priority-dot priority-high"></span>
                <span>High: Within 24 hours</span>
              </div>
              <div className="response-item">
                <span className="priority-dot priority-medium"></span>
                <span>Medium: Within 3 days</span>
              </div>
              <div className="response-item">
                <span className="priority-dot priority-low"></span>
                <span>Low: Within 1 week</span>
              </div>
            </div>
          </div>

          {/* <div className="help-section">
            <h4>📞 Alternative Contact</h4>
            <p>Need immediate help?</p>
            <div className="contact-info">
              <div>📧 support@jobsiterating.com</div>
              <div>📞 1-800-JOBSITE</div>
              <div>💬 Live Chat (9 AM - 5 PM)</div>
            </div>
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default Feedback; 